import React, { useState, useCallback, useMemo, useEffect } from 'react';

import { useIsAiBusy } from '@/features/chats/stores';
import { useFoldersWithGuidelines } from '@/shared/hooks';

import { MAX_SELECTED_GUIDELINES } from './ChatGuidelinesSelector.constants';
import type { ChatGuidelinesSelectorProps } from './ChatGuidelinesSelector.types';

export function useChatGuidelinesSelector({ value, onChange }: ChatGuidelinesSelectorProps) {
  const isAiBusy = useIsAiBusy();
  const [search, setSearch] = useState('');
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());

  const selectedCount = value.length;
  const isAtLimit = selectedCount >= MAX_SELECTED_GUIDELINES;

  // `useFoldersWithGuidelines` lives in `@/shared/hooks` (cross-entity
  // aggregator pattern, mirrors `useKnowledgeBasesList`). The previous
  // dependency injection `deps.useFoldersWithGuidelinesHook` injection is gone now
  // that the hook no longer crosses a feature boundary.
  const { data: foldersWithGuidelinesData } = useFoldersWithGuidelines();
  const foldersWithGuidelines = useMemo(
    () => foldersWithGuidelinesData ?? [],
    [foldersWithGuidelinesData],
  );

  useEffect(() => {
    if (foldersWithGuidelines.length === 1) {
      setExpandedFolders(new Set([foldersWithGuidelines[0].id]));
    }
  }, [foldersWithGuidelines]);

  const folderData = useMemo(
    () =>
      foldersWithGuidelines.map((item) => ({
        folder: { id: item.id, title: item.title },
        guidelines: item.guidelines,
      })),
    [foldersWithGuidelines],
  );

  const searchLower = search.toLowerCase().trim();

  const filteredFolderData = useMemo(() => {
    if (!searchLower) return folderData;
    return folderData
      .map((item) => {
        const folderMatches = item.folder.title.toLowerCase().includes(searchLower);
        const matchingGuidelines = item.guidelines.filter((g) =>
          g.name.toLowerCase().includes(searchLower),
        );
        if (folderMatches || matchingGuidelines.length > 0) {
          return {
            ...item,
            guidelines: folderMatches ? item.guidelines : matchingGuidelines,
          };
        }
        return null;
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  }, [folderData, searchLower]);

  const autoExpandedFolders = useMemo(() => {
    if (!searchLower) return new Set<string>();
    return new Set(filteredFolderData.map((item) => item.folder.id));
  }, [filteredFolderData, searchLower]);

  const isFolderExpanded = useCallback(
    (folderId: string) => {
      if (searchLower) return autoExpandedFolders.has(folderId);
      return expandedFolders.has(folderId);
    },
    [searchLower, autoExpandedFolders, expandedFolders],
  );

  const handleClose = useCallback(() => {
    setSearch('');
  }, []);

  const toggleFolder = useCallback((folderId: string) => {
    setExpandedFolders((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  }, []);

  const toggleGuideline = useCallback(
    (guidelineId: string) => {
      const isSelected = value.includes(guidelineId);
      if (!isSelected && value.length >= MAX_SELECTED_GUIDELINES) return;
      const next = isSelected ? value.filter((id) => id !== guidelineId) : [...value, guidelineId];
      onChange(next);
    },
    [value, onChange],
  );

  const allGuidelineIds = useMemo(
    () => folderData.flatMap((item) => item.guidelines.map((g) => g.id)),
    [folderData],
  );

  const guidelineIdsByFolder = useMemo(
    () => new Map(folderData.map((item) => [item.folder.id, item.guidelines.map((g) => g.id)])),
    [folderData],
  );

  const getFolderSelectionState = useCallback(
    (folderId: string): 'all' | 'some' | 'none' => {
      const ids = guidelineIdsByFolder.get(folderId) ?? [];
      if (ids.length === 0) return 'none';
      const selectedCount = ids.filter((id) => value.includes(id)).length;
      if (selectedCount === 0) return 'none';
      if (selectedCount === ids.length) return 'all';
      return 'some';
    },
    [guidelineIdsByFolder, value],
  );

  const toggleFolderGuidelines = useCallback(
    (folderId: string) => {
      const ids = guidelineIdsByFolder.get(folderId) ?? [];
      const state = getFolderSelectionState(folderId);
      // Treat indeterminate ('some') as "click to clear", matching standard
      // tri-state checkbox UX and giving users an escape hatch when at the limit.
      if (state === 'all' || state === 'some') {
        const idsSet = new Set(ids);
        onChange(value.filter((id) => !idsSet.has(id)));
      } else {
        const existing = new Set(value);
        const remaining = Math.max(0, MAX_SELECTED_GUIDELINES - value.length);
        const additions = ids.filter((id) => !existing.has(id)).slice(0, remaining);
        onChange([...value, ...additions]);
      }
    },
    [guidelineIdsByFolder, getFolderSelectionState, value, onChange],
  );

  const selectAll = useCallback(
    (e?: React.MouseEvent) => {
      if (e) {
        e.stopPropagation();
        e.preventDefault();
      }
      onChange(allGuidelineIds.slice(0, MAX_SELECTED_GUIDELINES));
    },
    [allGuidelineIds, onChange],
  );

  const clearAll = useCallback(
    (e?: React.MouseEvent) => {
      if (e) {
        e.stopPropagation();
        e.preventDefault();
      }
      onChange([]);
    },
    [onChange],
  );

  const clearSearch = useCallback(() => setSearch(''), []);

  return {
    isAiBusy,
    selectedCount,
    isAtLimit,
    search,
    filteredFolderData,
    isFolderExpanded,
    handleClose,
    toggleFolder,
    toggleGuideline,
    toggleFolderGuidelines,
    getFolderSelectionState,
    selectAll,
    clearAll,
    clearSearch,
    setSearch,
    value,
  };
}
